<template>
  <RemotePaginatedList
    :filter-options="filterOptions"
    :headers="headers"
    multi-sort
    v-model="selectedItemIds"
    data-url="admin/contract/"
    data-key="contracts"
    :refresh-trigger.sync="innerRefreshTrigger"
    v-bind="$attrs"
    class="contract-list"
  >
    <template #item.finalised_at="{ value }">
      <span :class="[{ finalised: value }, 'contract-finalised-span']">{{
        value ? "Finalised" : "In Progress"
      }}</span>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-row>
        <v-col>
          <v-btn
            icon
            color="primary"
            exact
            :to="{ name: 'contract-detail', params: { contractId: item.id } }"
          >
            INFO
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            icon
            color="primary"
            exact
            :to="{
              name: 'contract-work-orders',
              params: { contractId: item.id },
            }"
          >
            WORK ORDER
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            icon
            color="primary"
            v-if="canDeleteContract(item)"
            @click="deleteItem(item)"
          >
            Delete
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </RemotePaginatedList>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import RemotePaginatedList from "../RemotePaginatedList";
import _ from "lodash";
import { ACTION_TYPES } from "@/store/types";
import { PERMISSIONS } from "@/constants";

export default {
  name: "ContractList",
  components: { RemotePaginatedList },
  props: {
    value: Array,
    refreshTrigger: Boolean,
  },
  setup(props, { root, emit }) {
    const refresh = function () {
      innerRefreshTrigger.value = true;
    };
    onMounted(() => {
      refresh(false);
    });

    watch(
      () => props.refreshTrigger,
      (newValue) => {
        if (newValue) {
          refresh(true);
          emit("update:refresh-trigger", false);
        }
      }
    );

    // Filter
    const filterOptions = ref([
      { text: root.$t("contract.id"), value: "id", contains: true },
      { text: root.$t("contract.name"), value: "name", contains: true },
      {
        text: root.$t("contract.finalised_at"),
        value: "finalised_at",
        single: true,
        options: [
          { text: "In Progress", value: false },
          { text: "Finalised", value: true },
        ],
      },
    ]);

    // Data Table
    const headers = ref([
      { text: root.$t("contract.id"), value: "id", width: 200 },
      {
        text: root.$t("contract.finalised_at"),
        value: "finalised_at",
        width: 200,
        sortable: false,
      },
      { text: root.$t("contract.name"), value: "name" },
      { text: "", value: "actions", sortable: false, width: 320 },
    ]);
    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    watch(
      () => selectedItemIds.value,
      (newValue) => {
        emit("input", newValue);
      }
    );
    watch(
      () => props.value,
      (newValue, oldValue) => {
        if (_.xor(newValue, oldValue).length) {
          selectedItemIds.value = newValue;
        }
      },
      { deep: true, immediate: true }
    );

    const editItem = async function (item) {
      root.$router.push({ name: "sor-detail", params: { sorId: item.id } });
    };
    const exportPdf = async function (item) {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/section/export_pdf/",
        params: {
          section_id: item.id,
        },
        opt: { extra: { responseType: "arraybuffer" } },
      });

      let blob = new Blob([response.data], {
        type: response.headers.get("content-type"),
      });
      let link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${item.name}.pdf`;
      link.click();
    };

    const canDeleteContract = function (contract) {
      return (
        _.some([PERMISSIONS.MANAGE_CONTRACT], (p) =>
          root.$store.getters.hasPermission(p)
        ) || contract.created_by.id === root.$store.getters.account.id
      );
    };
    const deleteItem = async function (item) {
      if (await root.$dialog.confirm({ text: "Delete Contract?" })) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/contract/delete/",
          params: { contract_id: item.id },
        });

        if (response.body.success) {
          await refresh();
        }
      }
    };

    return {
      filterOptions,

      headers,
      innerRefreshTrigger,
      selectedItemIds,

      editItem,
      exportPdf,
      canDeleteContract,
      deleteItem,
    };
  },
};
</script>

<style lang="less">
.contract-list {
  .contract-finalised-span {
    color: rgba(108, 117, 125, 0.6);

    &.finalised {
      color: #5bc23f;
    }
  }
}
</style>
